import type { positionsApi } from '../api/client'
import type { ScorecardPosition } from '../types'
import type { Timeframe } from './timeframe'

export type PositionRow = Awaited<ReturnType<typeof positionsApi.getAll>>[number]

export interface PickBar {
  symbol: string
  returnPct: number
  pnlUsd: number
  /** Since-entry excess vs SPY as a PERCENT; null = unbenchmarked or DAY tier. */
  excessSinceEntryPct: number | null
}

/**
 * Bars for the Zone 1 picks chart (spec §5). DAY → broker intraday P&L;
 * any longer window → since-entry P&L with the scorecard's excess joined
 * per row (a single SPY reference line is meaningless across differing
 * entry dates, so since-entry mode carries a per-row excess instead).
 */
export function buildPickBars(
  tf: Timeframe,
  positions: PositionRow[],
  scorecard: ScorecardPosition[],
): { bars: PickBar[]; noIntradayCount: number } {
  const excessBySymbol = new Map(
    scorecard
      .filter((s) => s.state === 'held' && s.excess_vs_spy !== null)
      .map((s) => [s.symbol, (s.excess_vs_spy as number) * 100]),
  )

  let noIntradayCount = 0
  const bars: PickBar[] = []
  for (const p of positions) {
    if (tf === 'DAY') {
      if (p.unrealized_intraday_pnl_percent === null || p.unrealized_intraday_pnl === null) {
        noIntradayCount += 1
        continue
      }
      bars.push({
        symbol: p.symbol,
        returnPct: p.unrealized_intraday_pnl_percent,
        pnlUsd: p.unrealized_intraday_pnl,
        excessSinceEntryPct: null,
      })
    } else {
      bars.push({
        symbol: p.symbol,
        returnPct: p.unrealized_pnl_pct,
        pnlUsd: p.unrealized_pnl,
        excessSinceEntryPct: excessBySymbol.get(p.symbol) ?? null,
      })
    }
  }
  bars.sort((a, b) => b.returnPct - a.returnPct)
  return { bars, noIntradayCount }
}

/**
 * Book-level return for the selected window. DAY uses the flow-adjusted
 * daily_pnl from /api/account against the prior-close baseline (always
 * flow-adjusted). Longer windows are first-to-last over the (daily) equity
 * history, netted against `netFlow` (signed: +deposit / -withdrawal) when
 * known; `netFlow === null` means flows are UNKNOWN (distinct from a known
 * "0" — no flows), so the raw equity change is reported with the
 * `flowAdjusted` flag down so callers can caption the caveat.
 */
export function bookWindowReturn(
  tf: Timeframe,
  account: { daily_pnl: number; account_value: number },
  historyPoints: { t: string; equity: string }[],
  netFlow: number | null,
): { pct: number | null; usd: number | null; flowAdjusted: boolean } {
  if (tf === 'DAY') {
    const base = account.account_value - account.daily_pnl
    return {
      usd: account.daily_pnl,
      pct: base > 0 ? (account.daily_pnl / base) * 100 : null,
      flowAdjusted: true,
    }
  }
  if (historyPoints.length < 2) return { pct: null, usd: null, flowAdjusted: false }
  const first = Number(historyPoints[0].equity)
  const last = Number(historyPoints[historyPoints.length - 1].equity)
  if (!(first > 0)) return { pct: null, usd: null, flowAdjusted: false }
  const usd = netFlow !== null ? last - first - netFlow : last - first
  return { pct: (usd / first) * 100, usd, flowAdjusted: netFlow !== null }
}
